/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { agentsService } from '../../services/agents.service';
import { conversationsService } from '../../services/conversations.service';
import type { AccessibleProject } from '../../services/projects.service';
import type { Agent } from '../../types';
import type { Conversation } from '../../types/conversation.types';
import { extractErrorMessage } from '../../utils/error-handler';

interface Props {
  visible: boolean;
  projects: AccessibleProject[];
  onClose: () => void;
  onCreated: (conversation: Conversation) => void;
}

const NewConversationDialog = ({ visible, projects, onClose, onCreated }: Props) => {
  // Project is locked when the user has at most one: the agreed "project-first,
  // but only shown when there's a choice" convention from the agent screens.
  const projectLocked = projects.length <= 1;
  const [projectId, setProjectId] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentCode, setAgentCode] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset to a clean slate each time the dialog opens; default to the lone project.
  useEffect(() => {
    if (!visible) return;
    setError(null);
    setTitle('');
    setAgentCode(null);
    setProjectId(projects.length ? projects[0]._id : null);
  }, [visible, projects]);

  // Load the chosen project's agents whenever the project changes.
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setLoadingAgents(true);
    setAgents([]);
    const params: any = { page: 1, page_size: 200, include_global: true };
    if (projectId) params.app_id = projectId;
    agentsService.list(params)
      .then((res) => { if (active) setAgents(res.data || []); })
      .catch(() => { if (active) setAgents([]); })
      .finally(() => { if (active) setLoadingAgents(false); });
    return () => { active = false; };
  }, [visible, projectId]);

  // Prefer agents flagged chat-capable; fall back to all so the picker is never
  // empty just because the flag has not been set yet (the backend still enforces it).
  const chatAgents = useMemo(() => {
    const flagged = agents.filter((a) => (a as any).conversation?.enabled);
    return flagged.length ? flagged : agents;
  }, [agents]);

  const projectOptions = projects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }));
  const agentOptions = chatAgents.map((a) => ({ label: a.name ? `${a.name} (${a.agentCode})` : a.agentCode, value: a.agentCode }));

  let agentPlaceholder = 'Select an agent';
  if (loadingAgents) agentPlaceholder = 'Loading agents…';
  else if (!agentOptions.length) agentPlaceholder = 'No agents in this project';

  const handleCreate = async () => {
    if (!agentCode) { setError('Pick an agent to chat with.'); return; }
    try {
      setCreating(true);
      setError(null);
      const created = await conversationsService.create({
        agentCode,
        appId: projectId || undefined,
        title: title.trim() || undefined,
      });
      onCreated(created);
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not start the conversation'));
    } finally {
      setCreating(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button label="Cancel" severity="secondary" outlined size="small" onClick={onClose} disabled={creating} />
      <Button label={creating ? 'Starting…' : 'Start chat'} size="small" onClick={handleCreate}
        disabled={creating || !agentCode} icon={creating ? 'pi pi-spin pi-spinner' : 'pi pi-comments'} />
    </div>
  );

  return (
    <Dialog visible={visible} onHide={onClose} header="New conversation" footer={footer} style={{ width: '520px', maxWidth: '95vw' }}>
      <div data-testid="NewConversationDialog" className="flex flex-col gap-4">
        {!projectLocked && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Project</label>
            <Dropdown value={projectId} options={projectOptions} filter
              onChange={(e: any) => { setProjectId(e.value); setAgentCode(null); }}
              placeholder="Select project" data-testid="NewConversationDialog.Project" />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Agent</label>
          <Dropdown value={agentCode} options={agentOptions} filter
            onChange={(e: any) => setAgentCode(e.value)}
            placeholder={agentPlaceholder}
            disabled={loadingAgents} emptyMessage="No agents in this project" data-testid="NewConversationDialog.Agent" />
          <span className="text-xs text-ink-muted">Only agents configured for chat can answer; others will be refused.</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Title <span className="text-ink-muted">(optional)</span></label>
          <Input value={title} onChange={(v: string) => setTitle(v)} placeholder="Taken from your first message if left blank" />
        </div>

        {error && <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Dialog>
  );
};

export default NewConversationDialog;
